import { useState } from "react";
import Typewriter from "typewriter-effect";
import { Sparkles, X } from "lucide-react";
import { BaseModal } from "./BaseModal";
import { Spinner } from "../Spinner";

const LOADING_PHRASES = [
  "Sniffing out what you need...",
  "Building your basket...",
  "Checking the pantry...",
  "Bagging the essentials...",
  "Counting eggs before they hatch...",
];

export function AiShoppingListFillModal({ onGenerate, onAddItems, onClose }) {
  const [text, setText] = useState("");
  const [draftItems, setDraftItems] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  const isBusy = isGenerating || isAdding;

  const handleGenerate = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const items = await onGenerate(text);
      setDraftItems(items);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Couldn't generate items from that text. Please try again or add manually.",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAddItems = async () => {
    setIsAdding(true);
    setError(null);

    try {
      await onAddItems(draftItems);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Couldn't add those items to your list. Please try again.",
      );
    } finally {
      setIsAdding(false);
    }
  };

  const removeDraftItem = (index) => {
    setDraftItems((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <BaseModal
      isOpen={true}
      onClose={isBusy ? () => {} : onClose}
      title="Fill with AI"
      titleIcon={<Sparkles className="w-5 h-5" />}
      subtitle="Describe what you need and we'll turn it into shopping list items to review"
      footer={
        isGenerating
          ? false
          : draftItems === null
            ? (
                <>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerate}
                    disabled={!text.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
                  >
                    Generate
                  </button>
                </>
              )
            : (
                <>
                  <button
                    onClick={onClose}
                    disabled={isAdding}
                    className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  {draftItems.length > 0 && (
                    <button
                      onClick={handleAddItems}
                      disabled={isAdding}
                      className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2"
                    >
                      {isAdding && <Spinner size="sm" className="text-white" />}
                      {isAdding
                        ? "Adding..."
                        : `Add ${draftItems.length} item${draftItems.length === 1 ? "" : "s"}`}
                    </button>
                  )}
                </>
              )
      }
    >
      {isGenerating ? (
        <div className="flex flex-col items-center justify-center gap-4 py-8">
          <Spinner size="lg" className="text-gray-900" />
          <div className="text-sm font-medium text-gray-600 min-h-[20px] text-center">
            <Typewriter
              onInit={(typewriter) => {
                typewriter.pauseFor(1800).deleteAll().start();
              }}
              options={{ autoStart: true, loop: true, strings: LOADING_PHRASES }}
            />
          </div>
        </div>
      ) : draftItems === null ? (
        <>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={6}
            maxLength={1000}
            placeholder={`e.g. "eggs, milk, a loaf of bread, 2 avocados, stuff for tacos"`}
            className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
          />
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </>
      ) : draftItems.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-gray-600">
            Couldn't find any items in that text. Try rephrasing.
          </p>
          <button
            onClick={() => setDraftItems(null)}
            className="mt-3 text-sm font-medium text-gray-900 underline"
          >
            Back to editing
          </button>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500 mb-3">
            Review before adding to your list
          </p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {draftItems.map((item, index) => (
              <div
                key={`${item.itemName}-${index}`}
                className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <span className="text-gray-900">{item.itemName}</span>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500">
                    {item.itemQuantity} {item.itemUnit}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeDraftItem(index)}
                    aria-label={`Remove ${item.itemName}`}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
        </>
      )}
    </BaseModal>
  );
}
